/**
 * Terminology Provider (DRR-189)
 *
 * Provides industry-specific terminology translation to all child components.
 * Now powered by the statutory definition registry (resolvedTerminology query).
 *
 * The provider accepts EITHER:
 * - A pre-resolved terminology map (from useResolvedTerminology in the portal)
 * - A legacy branding object (backward compat during migration)
 *
 * Consumers use `useTerminology().translate()` — the API is unchanged.
 *
 * @example
 * ```tsx
 * const { translate } = useTerminology();
 * translate('workflow')  // → "Care Plan" (from statutory definition or tenant override)
 * translate('Patient')   // → "Resident" (from 144G.08 Subd 7)
 * ```
 */

import React, { createContext, useContext, useMemo } from 'react';

/**
 * Terminology context type
 */
export interface TerminologyContextType {
  /** Translate FERP/FHIR term to industry term */
  translate: (term: string) => string;
  /** Raw terminology mapping */
  terminology: Record<string, string>;
  /** Check if term has translation */
  hasTranslation: (term: string) => boolean;
  /** Get all translated terms */
  getAllTranslations: () => Record<string, string>;
}

/**
 * Terminology context
 */
const TerminologyContext = createContext<TerminologyContextType>({
  translate: (term: string) => term,
  terminology: {},
  hasTranslation: () => false,
  getAllTranslations: () => ({}),
});

/**
 * Provider props — accepts either resolved terminology map or legacy branding.
 */
export interface TerminologyProviderProps {
  /** DRR-189: Pre-resolved terminology map (concept → display). Preferred. */
  terminologyMap?: Record<string, string>;
  /** Legacy: Tenant branding with terminology mapping. Deprecated — use terminologyMap. */
  branding?: { terminology?: Record<string, string> } | null;
  /** Children components */
  children: React.ReactNode;
}

/**
 * Terminology Provider Component
 *
 * Provides terminology translation context to all child components.
 * DRR-189: Prefers terminologyMap (from resolvedTerminology query) over
 * legacy branding.terminology.
 */
export const TerminologyProvider: React.FC<TerminologyProviderProps> = ({
  terminologyMap,
  branding,
  children,
}) => {
  // DRR-189: Prefer resolved terminology map, fall back to legacy branding
  const terminology = useMemo(() => {
    if (terminologyMap && Object.keys(terminologyMap).length > 0) {
      return terminologyMap;
    }
    return branding?.terminology || {};
  }, [terminologyMap, branding?.terminology]);

  const contextValue = useMemo<TerminologyContextType>(() => {
    return {
      translate: (term: string) => terminology[term] || term,
      terminology,
      hasTranslation: (term: string) => term in terminology,
      getAllTranslations: () => ({ ...terminology }),
    };
  }, [terminology]);

  return (
    <TerminologyContext.Provider value={contextValue}>
      {children}
    </TerminologyContext.Provider>
  );
};

/**
 * Hook to access terminology translation.
 */
export const useTerminology = (): TerminologyContextType => {
  const context = useContext(TerminologyContext);

  if (!context) {
    throw new Error('useTerminology must be used within TerminologyProvider');
  }

  return context;
};

/**
 * Higher-order component for terminology translation.
 */
export function withTerminology<P extends object>(
  Component: React.ComponentType<P & { translate: (term: string) => string }>
): React.FC<P> {
  return (props: P) => {
    const { translate } = useTerminology();
    return <Component {...props} translate={translate} />;
  };
}
